<template>
  <div>
    <div class="wrapper" :endpoint="widget.endPoint">
      <div class="row bottom-border header-row" v-if="hasData && !loading">
        <span class="header divisions">{{ headerDivisionLabel }}</span>
        <span class="header escrow">Escrow</span>
        <span class="header transactionLimit text-right">Transaction Limit</span>
        <span class="header amount text-right">Total Limit</span>
        <span class="header balance text-right">Balance</span>
      </div>

      <div class="scroller" v-if="hasData && !loading">
        <div>
          <div v-for="(group, divisionName) in groupedItems" :key="divisionName">
            <div v-for="(escrow, index) in group" :key="index" class="row" :class="index === 0 ? 'delimiter' : ''">
              <span class="divisions">{{ index === 0 ? divisionName : '' }}</span>
              <span class="escrow">Escrow {{ divisionName === 'Clientwide' ? 0 : index + 1 }}</span>
              <span class="transactionLimit text-right">
                <v-money code :currency="escrow.CurrencyCode">{{ escrow.TransactionLimit }}</v-money>
              </span>
              <span class="amount text-right">
                <v-money code :currency="escrow.CurrencyCode">{{ escrow.TotalLimit }}</v-money>
              </span>
              <span class="balance text-right">
                <v-money code :currency="escrow.CurrencyCode">{{ escrow.Balance }}</v-money>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="empty-holder" v-if="loading">
        <span></span>
      </div>

      <div class="empty-holder" v-if="!hasData && !loading">
        <div class="content">
          <i class="fal fa-credit-card icon"></i>
          <span class="text">
            <p class="bold">No Data Found</p>
            <p class="light">No items are currently available</p>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { initScrollbar } from '../../helpers/scrollbarHelper.js'
  import { EventBus } from '@/event-bus'

  export default {
    alias: 'immediate-payment-escrows-wdg-cmp',
    props: { widget: Object },

    data() {
      return {
        items: [],
        loading: false
      }
    },

    computed: {
      hasData() {
        if ((this.items !== null) && (this.items.length > 0)) return true
        else return false
      },

      headerDivisionLabel() {
        return this.hasData
          ? this.items[0].DivisionLabel
          : 'Divisions'
      },

      // Group items by Division Name and return them sorted
      groupedItems() {
        const groups = this.items.reduce((groups, item) => {
          const divisionName = item.DivisionName || 'Clientwide'
          if (!groups[divisionName]) {
            groups[divisionName] = []
          }
          groups[divisionName].push(item)
          return groups
        }, {})
        return this.sortGroupedItems(groups)
      }
    },

    methods: {
      // Sort the grouped items and prioritize the Company Wide Settings (Division Name null)
      sortGroupedItems(groups) {
        const sortedKeys = Object.keys(groups).sort((a, b) => {
          const checkNullDivisionNameInKeyA = groups[a].some(item => item.DivisionName === null)
          const checkNullDivisionNameInKeyB = groups[b].some(item => item.DivisionName === null)

          if (checkNullDivisionNameInKeyA) return -1
          if (checkNullDivisionNameInKeyB) return 1
          return a.localeCompare(b)
        })

        return sortedKeys.reduce((sortedGroups, key) => {
          sortedGroups[key] = groups[key]
          return sortedGroups
        }, {})
      }
    },

    created() {
      this.loading = true

      this.api.get(this.widget.endPoint, {})
        .then(r => {
          this.items = r.data.items
        })
        .finally(() => {
          this.loading = false

          this.$nextTick(() => {
            let desiredHeight = !this.hasData ? 1 : getDesiredSize(this.items.length)

            EventBus.$emit('updateWidgetHeight', {
              wId: this.widget.id,
              height: desiredHeight,
              alt: -1
            })
            if (this.items.length > 6) {
              this.$el.querySelector('.scroller').style.height = '156px'
              initScrollbar(this, '.scroller')
            }
          })
        })
    }
  }

  function getDesiredSize(len) {
    let res = 1
    if (len >= 6) return 4
    switch (len) {
      case 1: res = 1
        break
      case 2:
      case 3: res = 2
        break
      case 4:
      case 5: res = 3
        break
    }
    return res
  }
</script>

<style lang="scss" scoped>
  @import "~@/assets/sass/_variables.scss";
  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 0.5rem 5px 0;
    font-size: 0.82rem;

    &.delimiter {
      border-top: 1px solid #ccc;
    }
  }

  .bottom-border {
    border-bottom: 2px solid #aaa;
  }

  .header-row {
    display: flex;
    flex-wrap: wrap;
    font-size: 0.87rem;
    padding-top: 0px;
  }

  .header {
    font-weight: bold;
  }

  .bold {
    font-weight: bold;
  }

  .light {
    font-weight: 300;
  }

  .divisions {
    flex: 2;
    padding: 0 0.5rem;
  }

  .escrow,
  .transactionLimit,
  .amount,
  .balance {
    flex: 1;
    padding: 0 0.5rem;
    min-width: 110px;
  }
  .escrow {
    min-width: 80px;
  }
  .transactionLimit {
    width: auto;
    min-width: 130px;
  }

  .text-right {
    text-align: right;
  }

  .scroller {
    position: relative;
    overflow: hidden;
  }

  .empty-holder {
    margin-top: 1rem;
    text-align: center;
    color: #999;

    .content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    .icon {
      font-size: 2rem;
      color: #666;
    }

    .text {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
    }

    .bold {
      font-weight: bold;
      font-size: 1.1rem;
    }

    .light {
      font-size: 1rem;
      color: #999;
    }
  }

  .wrapper {
    min-height: 30px;
    height: auto;
    max-height: 400px;

    &.loading:before {
      font-size: 28px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Dashboard/widgets/ImmediatePaymentEscrows/index.vue